/* eslint-disable max-lines */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import BulbIcon from '@mui/icons-material/Lightbulb';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import DateField from 'common/forms/DateField';
import { validateMinLength, validateTimeNotInFuture } from 'common/forms/formValidations';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import TimeField from 'common/forms/TimeField';
import useInfoDialog from 'common/hooks/useInfoDialog';
import { PrintLabelButton, PrintLabelProvider } from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';
import FacilityAvatar from 'common/modules/FacilityAvatar';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { EnableShippingService } from 'common/utils';
import ShipByDateCell from 'common/components/ShipByDateCell';
import { format } from 'date-fns';
import { shipmentMode } from 'enums/common.enum';
import {
  capitalize,
  compact,
  filter,
  find,
  first,
  forEach,
  includes,
  isBoolean,
  isEmpty,
  isNil,
  map,
  slice,
  some,
  sortBy,
  uniq,
  uniqBy,
} from 'lodash';
import TransferOrderButton from 'modules/PatientView/components/EligibilityActionItems/TransferOrderButton';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  createShipmentForLineItems,
  listBatchShipmentByPage,
  listShipmentLineItems,
} from 'store/thunks/batchShipmentThunk';
import { sendOrderBackToQueue } from 'store/thunks/patientThunks';
import { getFulfillingPharmacies } from 'store/thunks/pharmacyThunks';
import { v4 as uuidV4 } from 'uuid';
import ShipmentItemOption from './BatchShipmentItemOption';

const ShipmentModeWatcher = ({ form, shippingOptions, currentMode }) => {
  const savedOptionsRef = useRef({});
  const prevModeRef = useRef(currentMode);

  useEffect(() => {
    const prevMode = prevModeRef.current;
    if (prevMode === currentMode) return;

    const currentOption = form.getState().values.shippingOption;
    savedOptionsRef.current[prevMode] = currentOption;
    prevModeRef.current = currentMode;

    if (savedOptionsRef.current[currentMode]) {
      const availableOptions =
        currentMode === shipmentMode.PRX_SHIPMENT
          ? filter(shippingOptions, (opt) => opt.carrierCode.toLowerCase().includes('fedex'))
          : shippingOptions;
      const saved = savedOptionsRef.current[currentMode];
      const isSavedValid = availableOptions.some((opt) => opt.optionCode === saved);
      if (isSavedValid) {
        form.change('shippingOption', saved);
        return;
      }
    }

    const availableOptions =
      currentMode === shipmentMode.PRX_SHIPMENT
        ? filter(shippingOptions, (opt) => opt.carrierCode.toLowerCase().includes('fedex'))
        : shippingOptions;

    const isCurrentValid = availableOptions.some((opt) => opt.optionCode === currentOption);
    if (!isCurrentValid && availableOptions.length > 0) {
      const defaultOption = find(availableOptions, { isDefault: true }) || availableOptions[0];
      form.change('shippingOption', defaultOption.optionCode);
    }
  }, [currentMode, form, shippingOptions]);

  return null;
};

ShipmentModeWatcher.propTypes = {
  form: PropTypes.shape({
    getState: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
  }).isRequired,
  shippingOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentMode: PropTypes.string.isRequired,
};

const headCells = [
  {
    id: 'drugName',
    label: 'Drug Name',
  },
  {
    id: 'isOtc',
    label: 'Type',
  },
  {
    id: 'rxNumber',
    label: 'Rx Number',
  },
  {
    id: 'orderId',
    label: 'Order Id',
  },
  {
    id: 'itemStatus',
    label: 'Item Status',
  },
  {
    id: 'isTaggedForReview',
    label: 'Under Review',
  },
  {
    id: 'shippingOption',
    label: 'Shipping Option',
  },
  {
    id: 'location',
    label: 'Px',
  },
  {
    id: 'shipByDate',
    label: 'Ship By Date',
  },
  {
    id: 'patientName',
    label: 'Patient Name',
  },
];

const BatchShipmentsTable = () => {
  const hasAccess = useRoles();
  const dispatch = useDispatch();
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const itemsPerBox = Number(process.env.REACT_APP_BATCH_SHIPMENT_DEFAULT_SELECT) || 20;
  const lowerItemsPerBox = Number(process.env.REACT_APP_BATCH_SHIPMENT_LOWER_LIMIT) || 10;
  const theme = useTheme();
  const { pages, currentPage, count } = useSelector(({ batchShipment }) => batchShipment);
  const { shippingOptions } = useSelector(({ systemConfigs }) => systemConfigs);
  const { currentTenantId } = useSelector(({ auth }) => auth);
  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId] ?? []);
  const totalPages = Math.ceil(count / 25);
  const batchShipments = pages[currentPage];

  const [expandedPanelId, setExpandedPanelId] = useState(null);
  const [selectedForShipment, setSelectedForShipment] = useState([]);

  const isShippingToDifferentPatients = uniq(map(selectedForShipment, 'mpi')).length !== 1;
  const [remainingItems, setRemainingItems] = useState([]);
  const [addToBoxItems, setAddToBoxItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showShippingAlert, setShowShippingAlert] = useState(false);
  const [sendingAllBackToQueue, setSendingAllBackToQueue] = useState(false);

  const handleShippingAlertModalOpen = () => {
    setShowShippingAlert(true);
  };

  const handleCloseShippingModal = () => {
    setShowShippingAlert(false);
  };
  const shipmentData = find(batchShipments, (shipment) => {
    if (!expandedPanelId) return null;
    const argIds = expandedPanelId.split('+');
    const partnerId = argIds[1];

    return partnerId
      ? shipment.addressDirectoryId === argIds[0] &&
          shipment?.partnerId === argIds[1] &&
          shipment?.practiceId === argIds[2]
      : shipment.addressDirectoryId === argIds[0];
  });

  useMemo(() => {
    if (expandedPanelId && isEmpty(shipmentData?.lineItem?.shipmentLineItems)) {
      const argIds = expandedPanelId.split('+');

      dispatch(
        listShipmentLineItems({
          addressDirectoryId: argIds[0],
          partnerId: argIds[1],
          practiceId: argIds[2],
          currentPage,
        })
      );
    }
  }, [dispatch, expandedPanelId, currentPage, shipmentData]);

  const handleChange = (panel) => (_, isExpanded) => {
    if (panel !== expandedPanelId) {
      setExpandedPanelId(isExpanded ? panel : null);
    }
  };

  const handleSendAllBackToQueue = () => {
    showInfoDialog({
      title: 'Send All Back To Queue',
      message:
        'Are you sure you want to send all selected orders back to the queue? This cannot be undone.',
      isWarning: true,
      cancelable: true,
      confirmButtonText: 'Yes',
      callback: () => {
        const orderIds = uniq(map(selectedForShipment, 'orderId'));
        setSendingAllBackToQueue(true);
        dispatch(sendOrderBackToQueue({ orderIds, rollbackType: 'SINGLE' }))
          .then((response) => {
            if (response?.success) {
              const skippedOrders = response.skippedOrders || [];
              const skippedOrderIds = new Set(skippedOrders.map((skipped) => skipped.orderId));
              const processedCount = orderIds.filter((id) => !skippedOrderIds.has(id)).length;

              if (processedCount > 0) {
                enqueueSnackbar(
                  processedCount > 1
                    ? `${processedCount} orders sent back to queue successfully`
                    : 'Order sent back to queue successfully',
                  { variant: 'success' }
                );
              }

              if (skippedOrders.length > 0) {
                enqueueSnackbar(
                  `Skipped ${skippedOrders.length} order${
                    skippedOrders.length > 1 ? 's' : ''
                  }: ${skippedOrders
                    .map((skipped) => `${skipped.orderId} (${skipped.reason})`)
                    .join(', ')}`,
                  { variant: 'warning' }
                );
              }
            }
            const argIds = expandedPanelId.split('+');
            dispatch(
              listShipmentLineItems({
                addressDirectoryId: argIds[0],
                partnerId: argIds[1],
                practiceId: argIds[2],
                currentPage,
              })
            );
            setSelectedForShipment([]);
            setRemainingItems([]);
          })
          .finally(() => setSendingAllBackToQueue(false));
      },
    });
  };

  useEffect(() => {
    if (isEmpty(fulfillingPharmacies)) {
      dispatch(getFulfillingPharmacies());
    }
  }, [fulfillingPharmacies, dispatch]);

  useEffect(() => {
    if (!isEmpty(batchShipments) && !expandedPanelId) {
      const firstPanelId = `${first(batchShipments)?.addressDirectoryId}+${
        first(batchShipments)?.partnerId
      }+${first(batchShipments)?.practiceId}`;

      setExpandedPanelId(firstPanelId);
    }
  }, [batchShipments, expandedPanelId]);

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      return;
    }

    dispatch(listBatchShipmentByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isEmpty(batchShipments)) {
      const shipmentLineItems = shipmentData?.lineItem?.shipmentLineItems || [];

      const readyItems = shipmentLineItems.filter(
        (item) => item.lineItemStatus === 'READY_FOR_SHIPMENT'
      );
      const otherItems = shipmentLineItems.filter(
        (item) => item.lineItemStatus !== 'READY_FOR_SHIPMENT'
      );

      const sortedItems = [...readyItems, ...otherItems];
      setSelectedForShipment(slice(sortedItems, 0, itemsPerBox));
      setRemainingItems(slice(sortedItems, itemsPerBox));
    }
  }, [batchShipments, shipmentData, itemsPerBox]);

  const handleSelectedCheckboxChanged = (event) => {
    const uncheckedLineItemId = event.target.id;

    const lineItem = selectedForShipment.find(
      ({ lineItemId }) => lineItemId === uncheckedLineItemId
    );
    const updatedSelectedItems = selectedForShipment.filter(
      ({ lineItemId }) => lineItemId !== uncheckedLineItemId
    );
    remainingItems.push(lineItem);

    setRemainingItems(uniqBy(remainingItems, 'lineItemId'));
    setSelectedForShipment(uniqBy(updatedSelectedItems, 'lineItemId'));
  };

  const handleRemainingCheckBoxChanged = (event) => {
    const checkedLineItemId = event.target.id;
    const isChecked = event.target.checked;

    setAddToBoxItems((prevItems) => {
      if (isChecked) {
        const lineItem = remainingItems.find(({ lineItemId }) => lineItemId === checkedLineItemId);
        return uniqBy([...prevItems, lineItem], 'lineItemId');
      }

      return prevItems.filter(({ lineItemId }) => lineItemId !== checkedLineItemId);
    });
  };
  const handleSendToBox = () => {
    const updatedRemainingItems = remainingItems.filter(
      (remainingItem) =>
        !addToBoxItems.some(
          (toBoxLineItem) => toBoxLineItem.lineItemId === remainingItem.lineItemId
        )
    );

    forEach(addToBoxItems, (item) => {
      selectedForShipment.push(item);
    });

    setRemainingItems(uniqBy(compact(updatedRemainingItems), 'lineItemId'));
    setSelectedForShipment(uniqBy(selectedForShipment, 'lineItemId'));
    setAddToBoxItems([]);
  };

  const handleRemainingSelectAllChanged = (_, isChecked) =>
    setAddToBoxItems(isChecked ? remainingItems : []);

  const determineRemainingSelectAll = () => {
    return (
      remainingItems.some((item) =>
        addToBoxItems.some((boxItem) => boxItem.lineItemId === item.lineItemId)
      ) &&
      !remainingItems.every((item) =>
        addToBoxItems.some((boxItem) => boxItem.lineItemId === item.lineItemId)
      )
    );
  };

  const handleConfirm = (formData) => {
    setLoading(true);

    const itemsReadyForShipment = selectedForShipment.filter(
      (item) => item.lineItemStatus === 'READY_FOR_SHIPMENT'
    );

    const shipDateStr = formData?.shipDate
      ? format(new Date(formData.shipDate), 'yyyy-MM-dd')
      : null;
    const shipTimeStr = formData?.shipTime || null;

    const finalShipmentMode = formData.shipmentMode;
    const payload = {
      addressDirectoryId: first(expandedPanelId?.split('+')),
      lineItemIds: map(itemsReadyForShipment, 'lineItemId'),
      nameOnAddress: formData.nameOnAddress,
      shippingOption: formData.shippingOption,
      shipmentMode: formData.shipmentMode,
      shipDate: shipDateStr,
      shipTime: shipTimeStr,
      ...(finalShipmentMode === shipmentMode.EXTERNAL_SHIPMENT && {
        carrierCode: formData.carrierCode,
        trackingNumber: formData.trackingNumber,
        externalShipmentId: formData?.externalShipmentId ? formData?.externalShipmentId : uuidV4(),
      }),
    };

    dispatch(
      createShipmentForLineItems({
        ...payload,
      })
    )
      .then((response) => {
        if (response) {
          enqueueSnackbar(
            finalShipmentMode === shipmentMode.EXTERNAL_SHIPMENT
              ? 'Batch shipped successfully!'
              : 'Created Batch shipment',
            {
              variant: 'success',
            }
          );
          setShowModal(false);
          setLoading(false);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowModal(false);
    }
  };

  const handleOpenModal = () => setShowModal(true);

  const getNameOnAddress = () => {
    const itemsReadyForShipment = selectedForShipment.filter(
      (item) => item.lineItemStatus === 'READY_FOR_SHIPMENT'
    );

    if (shipmentData?.isBusinessAddress) {
      return shipmentData.practiceName;
    }

    const isSamePatientShipment = uniq(map(itemsReadyForShipment, 'patient.mpi')).length === 1;

    return isSamePatientShipment
      ? `${first(itemsReadyForShipment)?.patient.lastName}, ${
          first(itemsReadyForShipment)?.patient.firstName
        }`
      : '';
  };

  const getShippingOption = () => {
    const shipmentDetails = find(
      batchShipments,
      (shipment) => shipment?.addressDirectoryId === first(expandedPanelId?.split('+'))
    );

    const findMostFrequentString = (arr) => {
      const countMap = arr.reduce((acc, str) => {
        acc[str] = (acc[str] || 0) + 1;
        return acc;
      }, {});

      return arr.sort((a, b) => countMap[b] - countMap[a])[0];
    };

    const shippingOptionsVal = map(shipmentDetails?.lineItem?.shipmentLineItems, 'shippingOption');
    const option = findMostFrequentString(compact(shippingOptionsVal));

    return shippingOptions?.find((shippingOptionObj) => shippingOptionObj.optionCode === option)
      ?.optionCode;
  };

  const handleBatchTransferComplete = () => {
    dispatch(listBatchShipmentByPage({ page: currentPage }));
    setSelectedForShipment([]);
    setRemainingItems([]);
  };

  const transferEligibleLineItems = selectedForShipment.filter(
    (item) => item.lineItemStatus !== 'READY_FOR_SHIPMENT'
  );

  const defaultShipmentMode =
    find(fulfillingPharmacies, {
      fulfillingPharmacyId: first(selectedForShipment)?.fulfillingPharmacyId,
    })?.shipmentConfig?.shipmentRoutes?.routeTo ?? shipmentMode.SHIP_VIA_SHIPSTATION;

  return (
    <>
      <InfoDialog />

      <Dialog open={showShippingAlert}>
        <DialogContent>
          <Grid>
            <Typography sx={{ fontSize: '1rem' }}>
              There are less than {lowerItemsPerBox} items in this batch, that are being shipped to
              different patients. Please consider unbatching them and sending these as individual
              shipments
            </Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={handleCloseShippingModal} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showModal} aria-describedby='BatchShipment-createShipment-Header' fullWidth>
        <DialogTitle id='BatchShipment-createShipment-Header' sx={{ pb: 0 }}>
          Confirm Batch Shipment
        </DialogTitle>

        <Form
          onSubmit={handleConfirm}
          initialValues={{
            nameOnAddress: getNameOnAddress(),
            shippingOption: getShippingOption(),
            carrierCode: 'UPS',
            shipmentMode: defaultShipmentMode,
          }}
          render={({ handleSubmit, invalid, values, form }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <Grid>
                  <Typography variant='body2' sx={{ pb: 2 }}>
                    Please note that items that are not ready for shipment will not be included in
                    the shipment.
                  </Typography>
                </Grid>
                <TextField
                  id='ConfirmShipment-nameOnAddress-input'
                  label='Name On Address'
                  name='nameOnAddress'
                  required
                  autoFocus
                  validations={[validateMinLength('Minimum of 3 characters', 3)]}
                />
                <SelectField
                  id='BatchShipments-shippingService-dropdown'
                  label='Shipping Service'
                  name='shipmentMode'
                  lexicographicSort={false}
                >
                  <MenuItem value={shipmentMode.SHIP_VIA_SHIPSTATION}>
                    Ship Via ShipStation
                  </MenuItem>
                  {EnableShippingService && (
                    <MenuItem value={shipmentMode.PRX_SHIPMENT}>Send to Shipping</MenuItem>
                  )}
                  <MenuItem value={shipmentMode.EXTERNAL_SHIPMENT}>
                    Enter Shipment Manually
                  </MenuItem>
                </SelectField>

                <ShipmentModeWatcher
                  form={form}
                  shippingOptions={shippingOptions}
                  currentMode={values.shipmentMode}
                />
                <SelectField
                  id='ConfirmShipment-shippingOptions-dropdown'
                  label='Shipping Option'
                  name='shippingOption'
                  required
                >
                  {map(
                    values.shipmentMode === shipmentMode.PRX_SHIPMENT
                      ? filter(shippingOptions, (option) =>
                          option.carrierCode.toLowerCase().includes('fedex')
                        )
                      : shippingOptions,
                    ({ shippingOption, optionCode }, key) => {
                      return (
                        <MenuItem value={optionCode} key={key}>
                          {shippingOption}
                        </MenuItem>
                      );
                    }
                  )}
                </SelectField>

                {values.shipmentMode === shipmentMode.EXTERNAL_SHIPMENT && (
                  <Grid item xs={12}>
                    <SelectField
                      id='Shipment-Manually-dropdown'
                      label='Carrier Code'
                      name='carrierCode'
                      value='UPS'
                      disabled
                    >
                      <MenuItem value='UPS'>UPS</MenuItem>
                    </SelectField>

                    <TextField label='Tracking Number' name='trackingNumber' required />

                    <DateField
                      label='Shipping Date'
                      name='shipDate'
                      id='BatchShipmentsTable-shipDate-dateField'
                      required
                      maxDate={new Date()}
                    />

                    <TimeField
                      label='Shipping Time (24-hour)'
                      name='shipTime'
                      id='BatchShipmentsTable-shipTime-timeField'
                      required
                      helperText='Use 24-hour format, e.g., 14:30'
                      validations={[
                        validateTimeNotInFuture('Time cannot be in the future', 'shipDate'),
                      ]}
                    />

                    <TextField
                      label='External Shipment ID (If Available)'
                      name='externalShipmentId'
                    />
                  </Grid>
                )}
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={loading}
                  variant='contained'
                  disabled={invalid}
                  type='submit'
                >
                  Confirm
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          minHeight: '100vh',
          width: '100%',
        }}
      >
        <Grid sx={{ py: 2, px: 2 }} container direction='column'>
          {map(batchShipments, (batchShipment) => {
            const shipmentLineItem = batchShipment?.lineItem;
            const shipmentLineItems = shipmentLineItem?.shipmentLineItems;

            const isSomeLineItemsReady = !selectedForShipment.some(
              (item) => item.lineItemStatus === 'READY_FOR_SHIPMENT'
            );

            const allItemsReadyForShipment =
              !isEmpty(shipmentLineItems) &&
              shipmentLineItems.every((item) => item.lineItemStatus === 'READY_FOR_SHIPMENT');

            const selectedOrderIds = map(selectedForShipment, 'orderId');
            const isSelectedOrderInReview = some(
              shipmentLineItems,
              ({ orderId, isTaggedForReview }) =>
                isTaggedForReview && includes(selectedOrderIds, orderId)
            );

            return (
              <Accordion
                sx={{ marginTop: '10px' }}
                key={`${batchShipment.addressDirectoryId}+${batchShipment?.partnerId}+${batchShipment?.practiceId}`}
                expanded={
                  expandedPanelId ===
                  `${batchShipment.addressDirectoryId}+${batchShipment?.partnerId}+${batchShipment?.practiceId}`
                }
                onChange={handleChange(
                  `${batchShipment.addressDirectoryId}+${batchShipment?.partnerId}+${batchShipment?.practiceId}`
                )}
              >
                <AccordionSummary
                  sx={{ bgcolor: lighten(theme.palette.text.primary, 0.9) }}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Grid container justifyContent='space-between' alignItems='center'>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Typography variant='h6' fontWeight='bold'>
                        {compact([
                          batchShipment.address.addressLine1,
                          batchShipment.address.addressState,
                        ])
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                      <Tooltip
                        title={compact([
                          batchShipment.address.addressLine1,
                          batchShipment.address.addressLine2,
                          batchShipment.address.addressCity,
                          batchShipment.address.addressState,
                          batchShipment.address.addressZip,
                        ])
                          .filter(Boolean)
                          .join(', ')}
                        arrow
                        placement='top'
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '20px',
                            },
                          },
                        }}
                      >
                        <InfoIcon />
                      </Tooltip>

                      {batchShipment?.partnerName && (
                        <>
                          <Typography variant='h6' fontWeight='bold'>
                            | {capitalize(batchShipment?.partnerName)}
                          </Typography>

                          <Tooltip
                            title='Partner Name'
                            arrow
                            placement='top'
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '20px',
                                },
                              },
                            }}
                          >
                            <InfoIcon />
                          </Tooltip>
                        </>
                      )}

                      {batchShipment?.practiceName && (
                        <>
                          <Typography variant='h6' fontWeight='bold'>
                            | {capitalize(batchShipment?.practiceName)}
                          </Typography>

                          <Tooltip
                            title='Practice Name'
                            arrow
                            placement='top'
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '20px',
                                },
                              },
                            }}
                          >
                            <InfoIcon />
                          </Tooltip>
                        </>
                      )}
                    </Stack>

                    <Stack sx={{ pr: 2 }} direction='row' alignItems='center' spacing={2}>
                      <Chip
                        sx={{
                          background: theme.palette.secondary.light,
                          color: theme.palette.primary.contrastText,
                          maxHeight: '80%',
                        }}
                        label={
                          <Typography variant='body2'>
                            Items in Shipment: {batchShipment.totalItems}
                          </Typography>
                        }
                      />

                      <TransferOrderButton
                        orderIds={uniq(map(transferEligibleLineItems, 'orderId'))}
                        lineItems={map(transferEligibleLineItems, (lineItem) => ({
                          lineItemId: lineItem.lineItemId,
                          itemName: lineItem.drugName,
                          rxNumber: lineItem.rxNumber,
                          processingState: lineItem.processingState,
                        }))}
                        onBatchTransferComplete={handleBatchTransferComplete}
                        showLineItemSelection={false}
                        sendLineItemIdsOnly
                        disabled={allItemsReadyForShipment}
                        disabledTooltip='Order is not in appropriate state'
                      />
                    </Stack>
                  </Grid>
                </AccordionSummary>

                {isNil(shipmentLineItems) && (
                  <AccordionDetails>
                    <Grid sx={{ my: 1 }} container direction='column'>
                      <LoadingModule />
                    </Grid>
                  </AccordionDetails>
                )}

                {!isNil(shipmentLineItems) && isEmpty(shipmentLineItems) && (
                  <AccordionDetails>
                    <Grid sx={{ my: 1 }} container direction='column'>
                      <NothingFound />
                    </Grid>
                  </AccordionDetails>
                )}

                {!isEmpty(shipmentLineItems) && (
                  <AccordionDetails>
                    <Grid
                      sx={{ my: 1, p: 2, border: '1px solid black' }}
                      container
                      direction='column'
                    >
                      <Grid item>
                        <Grid
                          container
                          justifyContent='space-between'
                          alignItems='center'
                          sx={{ mb: 1 }}
                        >
                          <Chip
                            sx={{
                              background: theme.palette.secondary.light,
                              color: theme.palette.primary.contrastText,
                              maxHeight: '80%',
                              height: '30px',
                              borderRadius: '3px',
                            }}
                            label={
                              <Typography variant='h7'>Box Capacity: {itemsPerBox}</Typography>
                            }
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {selectedForShipment.length < lowerItemsPerBox &&
                              isShippingToDifferentPatients &&
                              !isSomeLineItemsReady && (
                                <BulbIcon
                                  onClick={handleShippingAlertModalOpen}
                                  sx={{
                                    width: { xs: 24, sm: 32, md: 40, lg: 35 },
                                    height: { xs: 24, sm: 32, md: 40, lg: 50 },
                                    marginBottom: '2px',
                                    flexShrink: 0,
                                    animation: 'fillBlink 1.5s infinite',
                                    '@keyframes fillBlink': {
                                      '0%': { opacity: 1 },
                                      '50%': { opacity: 0.3 },
                                      '100%': { opacity: 1 },
                                    },
                                    transition: 'opacity 0.3s ease-in-out',
                                  }}
                                  style={{
                                    color: perfectRxTheme.palette.warning.warningColour,
                                  }}
                                />
                              )}
                            {hasAccess.generateBulkLabels && (
                              <PrintLabelProvider
                                key={map(
                                  sortBy(selectedForShipment, 'shipByDate'),
                                  'lineItemId'
                                ).join(',')}
                                initialValue={map(
                                  sortBy(selectedForShipment, 'shipByDate'),
                                  'lineItemId'
                                )}
                                lineItems={selectedForShipment}
                              >
                                <PrintLabelButton isBulk isBatchShipment />
                              </PrintLabelProvider>
                            )}
                            <Tooltip
                              arrow
                              placement='top'
                              title={
                                isSelectedOrderInReview
                                  ? 'Orders in review cannot be sent back to the queue'
                                  : ''
                              }
                            >
                              <span>
                                <LoadingButton
                                  variant='outlined'
                                  color='warning'
                                  loading={sendingAllBackToQueue}
                                  disabled={isSelectedOrderInReview}
                                  onClick={handleSendAllBackToQueue}
                                >
                                  Send All Back To Queue
                                </LoadingButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              arrow
                              placement='top'
                              title={`${
                                isSomeLineItemsReady ? 'Items not ready for shipment' : ''
                              }`}
                            >
                              <span>
                                <Button
                                  variant='contained'
                                  disabled={isEmpty(selectedForShipment) || isSomeLineItemsReady}
                                  onClick={handleOpenModal}
                                >
                                  Send to Shipment
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <TableContainer>
                          <Table size='small' sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                              <TableRow>
                                {headCells.map((headCell) => (
                                  <TableCell key={headCell.id} align='center'>
                                    <strong>{headCell.label}</strong>
                                  </TableCell>
                                ))}
                                <TableCell />
                                <TableCell />
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {map(sortBy(selectedForShipment, 'shipByDate'), (item) => {
                                return (
                                  <TableRow
                                    key={`${item.lineItemId}`}
                                    sx={{
                                      backgroundColor: lighten(theme.palette.primary.light, 0.8),
                                    }}
                                  >
                                    <TableCell sx={{ paddingY: '12px' }} align='center'>
                                      {item.drugName}
                                    </TableCell>
                                    <TableCell align='center'>
                                      {isBoolean(item.isOtc) && item.isOtc ? 'OTC' : 'RX'}
                                    </TableCell>
                                    <TableCell sx={{ paddingY: '12px' }} align='center'>
                                      {item.rxNumber}
                                    </TableCell>
                                    <TableCell align='center'>
                                      <Link
                                        to={`/orders/${item.orderId}`}
                                        style={{ color: theme.palette.text.primary }}
                                      >
                                        {item.orderId}
                                      </Link>
                                    </TableCell>
                                    <TableCell align='center'>
                                      {item.lineItemStatus.replace(/_/g, ' ')}
                                    </TableCell>
                                    <TableCell align='center'>
                                      {item.isTaggedForReview ? 'Yes' : 'No'}
                                    </TableCell>
                                    <TableCell sx={{ paddingY: '12px' }} align='center'>
                                      {find(
                                        shippingOptions,
                                        (option) => option.optionCode === item.shippingOption
                                      )?.shippingOption || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item?.rxPrefix ? (
                                        <Grid container justifyContent='center'>
                                          <FacilityAvatar
                                            rxPrefix={item?.rxPrefix}
                                            facilityName={item?.facilityName}
                                            facilityState={item?.fulfillingPharmacyState}
                                          />
                                        </Grid>
                                      ) : (
                                        <Grid />
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ paddingY: '12px' }} align='center'>
                                      <ShipByDateCell
                                        shipByDate={item.shipByDate}
                                        tags={item.tags}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ paddingY: '12px' }} align='center'>
                                      <Link
                                        to={`/patients/${item.patient.mpi}`}
                                        style={{ color: theme.palette.text.primary }}
                                      >
                                        {`${item.patient.lastName}, ${item.patient.firstName} `}
                                      </Link>
                                    </TableCell>
                                    <TableCell align='center'>
                                      <Tooltip
                                        arrow
                                        placement='right'
                                        title={`${
                                          item.lineItemStatus !== 'READY_FOR_SHIPMENT'
                                            ? 'Item not ready for shipment'
                                            : ''
                                        }`}
                                      >
                                        <span>
                                          <Checkbox
                                            checked={map(
                                              selectedForShipment,
                                              'lineItemId'
                                            ).includes(item.lineItemId)}
                                            id={item.lineItemId}
                                            onChange={handleSelectedCheckboxChanged}
                                            disabled={
                                              selectedForShipment.length <= 1 ||
                                              item.lineItemStatus !== 'READY_FOR_SHIPMENT'
                                            }
                                          />
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align='center'>
                                      <ShipmentItemOption
                                        addressDirectoryId={batchShipment.addressDirectoryId}
                                        lineItemId={item.lineItemId}
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>

                    <Grid sx={{ my: 1, mt: 4 }} container direction='column'>
                      <Grid item>
                        <TableContainer>
                          <Table size='small' sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                              <TableRow>
                                {headCells.map((headCell) => (
                                  <TableCell key={headCell.id} align='center'>
                                    <strong>{headCell.label}</strong>
                                  </TableCell>
                                ))}
                                <TableCell align='center'>
                                  <Checkbox
                                    indeterminate={determineRemainingSelectAll()}
                                    checked={
                                      remainingItems.length > 0 &&
                                      remainingItems.every((item) =>
                                        addToBoxItems.some(
                                          (boxItem) => boxItem.lineItemId === item.lineItemId
                                        )
                                      )
                                    }
                                    onChange={handleRemainingSelectAllChanged}
                                  />
                                </TableCell>
                                <TableCell align='center'>
                                  <Tooltip
                                    arrow
                                    placement='top'
                                    title={`${
                                      selectedForShipment.length === itemsPerBox ||
                                      selectedForShipment.length + addToBoxItems.length >
                                        itemsPerBox
                                        ? 'Maximum items added(selected) to box for shipment'
                                        : ''
                                    }`}
                                  >
                                    <span>
                                      <Button
                                        variant='contained'
                                        sx={{ mr: 2 }}
                                        disabled={
                                          isEmpty(addToBoxItems) ||
                                          selectedForShipment.length === itemsPerBox ||
                                          selectedForShipment.length + addToBoxItems.length >
                                            itemsPerBox
                                        }
                                        onClick={handleSendToBox}
                                      >
                                        Send to Box
                                      </Button>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {map(sortBy(remainingItems, 'shipByDate'), (item) => (
                                <TableRow key={`${item.lineItemId}`} hover>
                                  <TableCell sx={{ paddingY: '12px' }} align='center'>
                                    {item.drugName}
                                  </TableCell>
                                  <TableCell align='center'>
                                    {isBoolean(item.isOtc) && item.isOtc ? 'OTC' : 'RX'}
                                  </TableCell>
                                  <TableCell sx={{ paddingY: '12px' }} align='center'>
                                    {item.rxNumber}
                                  </TableCell>
                                  <TableCell align='center'>
                                    <Link
                                      to={`/orders/${item.orderId}`}
                                      style={{ color: theme.palette.text.primary }}
                                    >
                                      {item.orderId}
                                    </Link>
                                  </TableCell>
                                  <TableCell align='center'>
                                    {item.lineItemStatus.replace(/_/g, ' ')}
                                  </TableCell>
                                  <TableCell align='center'>
                                    {item.isTaggedForReview ? 'Yes' : 'No'}
                                  </TableCell>
                                  <TableCell align='center'>
                                    {find(
                                      shippingOptions,
                                      (option) => option.optionCode === item.shippingOption
                                    )?.shippingOption || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {item?.rxPrefix ? (
                                      <Grid container justifyContent='center'>
                                        <FacilityAvatar
                                          rxPrefix={item?.rxPrefix}
                                          facilityName={item?.facilityName}
                                          facilityState={item?.fulfillingPharmacyState}
                                        />
                                      </Grid>
                                    ) : (
                                      <Grid />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ paddingY: '12px' }} align='center'>
                                    {item.needsByDate}
                                  </TableCell>
                                  <TableCell sx={{ paddingY: '12px' }} align='center'>
                                    <Link
                                      to={`/patients/${item.patient.mpi}`}
                                      style={{ color: theme.palette.text.primary }}
                                    >
                                      {`${item.patient.lastName}, ${item.patient.firstName} `}
                                    </Link>
                                  </TableCell>
                                  <TableCell align='center'>
                                    <Tooltip
                                      arrow
                                      placement='right'
                                      title={`${
                                        item.lineItemStatus !== 'READY_FOR_SHIPMENT'
                                          ? 'Item not ready for shipment'
                                          : ''
                                      }`}
                                    >
                                      <span>
                                        <Checkbox
                                          checked={includes(
                                            map(addToBoxItems, 'lineItemId'),
                                            item.lineItemId
                                          )}
                                          onChange={handleRemainingCheckBoxChanged}
                                          id={item.lineItemId}
                                          disabled={item.lineItemStatus !== 'READY_FOR_SHIPMENT'}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align='center'>
                                    <ShipmentItemOption
                                      addressDirectoryId={batchShipment.addressDirectoryId}
                                      lineItemId={item.lineItemId}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                )}
              </Accordion>
            );
          })}

          {!isNil(batchShipments) && totalPages > 1 && (
            <TableFooter
              sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
            >
              <TableRow>
                <TableCell colSpan={8} align='right' sx={{ borderBottom: 'none' }}>
                  <Pagination
                    sx={{
                      justifyContent: 'flex-end',
                      alignItems: 'flex-end',
                      display: 'flex',
                    }}
                    count={totalPages}
                    page={currentPage}
                    size='small'
                    onChange={handlePageChange}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}

          {isNil(batchShipments) && (
            <Grid item>
              <LoadingModule />
            </Grid>
          )}

          {!isNil(batchShipments) && isEmpty(batchShipments) && (
            <Grid item>
              <NothingFound />
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default BatchShipmentsTable;
